import { icon } from '../icons.mjs';

/** Session-local, explicit control preferences. No device/account persistence.
 * Both input owners read the same snapshot. Stable native buttons keep focus
 * while preferences repaint; the caller releases held actions on each change. */
export function createSkyControlSettings({ e, listen, onChange = () => {} }) {
  const element = e('fieldset', 'sky-control-settings');
  element.append(e('legend', 'sky-control-legend', 'Flight controls'));
  const board = e('div', 'sky-control-board');
  element.append(board);
  let settings = {
    aimSensitivity: 1,
    moveSensitivity: 1,
    aimInvertX: false,
    aimInvertY: false,
    moveInvertX: false,
    moveInvertY: false,
    mirrored: false,
    assistance: null,
  };
  const painters = [];
  function symbol(name, className = 'sky-control-symbol') {
    const node = e('span', `${className}${name === 'plus' ? ' sky-control-reticle' : ''}`);
    node.setAttribute('aria-hidden', 'true');
    // Text remains readable in the existing non-SVG test/accessibility host.
    if (typeof globalThis.document?.createElementNS === 'function') node.append(icon(name));
    return node;
  }
  function change(key, value) {
    settings = { ...settings, [key]: value };
    for (const paint of painters) paint();
    onChange({ ...settings });
  }
  function card(key, title, caption, glyph) {
    const section = e('section', 'sky-control-card');
    section.dataset.control = key;
    section.setAttribute('aria-label', `${title} controls`);
    const heading = e('header', 'sky-control-card-heading');
    const copy = e('div', 'sky-control-card-copy');
    copy.append(
      e('strong', 'sky-control-title', title),
      e('small', 'sky-control-caption', caption),
    );
    heading.append(symbol(glyph, 'sky-control-card-icon'), copy);
    section.append(heading);
    board.append(section);
    return section;
  }
  function row(section, title) {
    const node = e('div', 'sky-control-row');
    node.append(e('span', 'sky-control-row-title', title));
    section.append(node);
    return node;
  }
  function choice(key, value, title, glyph, label, className = 'sky-control-choice') {
    const button = e('button', className);
    button.type = 'button';
    button.dataset.setting = key;
    button.dataset.value = value === null ? 'auto' : String(value);
    button.setAttribute('aria-label', label);
    button.title = label;
    button.append(symbol(glyph), e('span', 'sky-control-choice-label', title));
    painters.push(() => button.setAttribute('aria-pressed', String(settings[key] === value)));
    listen(button, 'click', () => change(key, value));
    return button;
  }
  function sensitivity(section, key, label) {
    const group = e('div', 'sky-control-segments');
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', `${label} sensitivity`);
    for (const [value, name, glyph] of [
      [0.6, 'Soft', 'leaf'],
      [1, 'Normal', 'circle-check'],
      [1.5, 'Fast', 'zap'],
      [2, 'Max', 'sparkles'],
    ])
      group.append(
        choice(key, value, name, glyph, `${label} sensitivity: ${name}, ${value} times normal`),
      );
    row(section, 'Sensitivity').append(group);
  }
  function inversions(section, prefix, label) {
    const group = e('div', 'sky-control-flips');
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', `${label} axis inversion`);
    for (const [axis, suffix, glyphs] of [
      ['horizontal', 'X', ['arrow-left', 'arrow-right']],
      ['vertical', 'Y', ['arrow-up', 'arrow-down']],
    ]) {
      const key = `${prefix}Invert${suffix}`;
      const button = e('button', 'sky-control-flip');
      button.type = 'button';
      button.dataset.setting = key;
      button.dataset.axis = axis;
      button.setAttribute('aria-label', `Invert ${label.toLowerCase()} ${axis} axis`);
      button.title = `Reverse ${axis} ${label.toLowerCase()}`;
      const arrows = e('span', 'sky-control-axis-symbol');
      arrows.setAttribute('aria-hidden', 'true');
      for (const glyph of glyphs) arrows.append(symbol(glyph));
      const state = e('small', 'sky-control-toggle-state');
      state.setAttribute('aria-hidden', 'true');
      button.append(
        arrows,
        e('span', 'sky-control-choice-label', axis === 'horizontal' ? 'Horizontal' : 'Vertical'),
        state,
      );
      painters.push(() => {
        button.setAttribute('aria-pressed', String(settings[key]));
        state.textContent = settings[key] ? 'ON' : 'OFF';
      });
      listen(button, 'click', () => change(key, !settings[key]));
      group.append(button);
    }
    row(section, 'Reverse axes').append(group);
  }
  const move = card('move', 'MOVE', 'Steer the pilot', 'hand');
  sensitivity(move, 'moveSensitivity', 'Movement');
  inversions(move, 'move', 'Movement');
  const aim = card('aim', 'AIM', 'Place the crosshair', 'plus');
  sensitivity(aim, 'aimSensitivity', 'Aim');
  inversions(aim, 'aim', 'Aim');
  const assistance = e('div', 'sky-control-segments');
  assistance.setAttribute('role', 'group');
  assistance.setAttribute('aria-label', 'Aim assistance');
  for (const [value, name, glyph, label] of [
    [
      null,
      'Auto',
      'sparkles',
      'Default aim assistance: Standard for touch and keyboard, Light for mouse',
    ],
    ['off', 'Off', 'x', 'Aim assistance off'],
    ['light', 'Light', 'star', 'Light aim assistance'],
    ['standard', 'Standard', 'plus', 'Standard aim assistance'],
  ])
    assistance.append(choice('assistance', value, name, glyph, label));
  row(aim, 'Assistance').append(assistance);
  const layout = card('layout', 'LAYOUT', 'Choose your thumb positions', 'gamepad-2');
  const layouts = e('div', 'sky-control-layouts');
  layouts.setAttribute('role', 'group');
  layouts.setAttribute('aria-label', 'Touch control layout');
  for (const mirrored of [false, true]) {
    const name = mirrored ? 'Swapped' : 'Standard';
    const button = e('button', 'sky-control-layout');
    button.type = 'button';
    button.dataset.setting = 'mirrored';
    button.dataset.value = mirrored ? 'mirrored' : 'normal';
    button.setAttribute(
      'aria-label',
      mirrored
        ? 'Swapped layout: aim on the left, move on the right'
        : 'Standard layout: move on the left, aim on the right',
    );
    const preview = e('div', 'sky-control-layout-preview');
    preview.setAttribute('aria-hidden', 'true');
    for (const role of mirrored ? ['aim', 'move'] : ['move', 'aim']) {
      const pad = e('span', `sky-control-pad is-${role}`);
      pad.append(symbol(role === 'move' ? 'hand' : 'plus'), e('b', '', role.toUpperCase()));
      preview.append(pad);
    }
    button.append(preview, e('span', 'sky-control-choice-label', name));
    painters.push(() =>
      button.setAttribute('aria-pressed', String(settings.mirrored === mirrored)),
    );
    listen(button, 'click', () => change('mirrored', mirrored));
    layouts.append(button);
  }
  layout.append(layouts);
  const disclosure = e('details', 'sky-control-disclosure');
  const summary = e('summary', 'sky-control-summary');
  summary.append(
    symbol('settings'),
    e('span', 'sky-control-summary-title', 'CONTROLS'),
    e('small', 'sky-control-summary-hint', 'Tune your flight'),
    symbol('chevron-down', 'sky-control-summary-chevron'),
  );
  const footer = e('footer', 'sky-control-footer');
  const done = e('button', 'sky-control-done');
  done.type = 'button';
  done.setAttribute('aria-label', 'Done with flight controls. Return to the flight menu.');
  done.append(symbol('check'), e('span', '', 'DONE'));
  listen(done, 'click', () => {
    disclosure.open = false;
    summary.focus();
  });
  listen(disclosure, 'keydown', (event) => {
    if (event.key !== 'Escape' || !disclosure.open) return;
    event.preventDefault();
    disclosure.open = false;
    summary.focus();
  });
  footer.append(e('p', 'sky-control-note', 'This play session'), done);
  element.append(footer);
  disclosure.append(summary, element);
  for (const paint of painters) paint();
  return { element, disclosure, getSettings: () => ({ ...settings }) };
}
